/**
 * DealerScorecardPage — admin engineering quality scorecard.
 *
 * Aggregates the engineering-review queue by dealer (head of the
 * ResellerID chain captured at commissioning time). Surfaces approval
 * ratios per dealer and red-flags those below 50%.
 *
 * Route: /admin/commissioning/scorecard
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";

export default function DealerScorecardPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/commissioning/engineering/scorecard");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load scorecard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="scorecard-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("aux.back_home_tooltip")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <ChartBar size={20} weight="fill" className="text-rose-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="scorecard-title">
          {t("scorecard.page_title")}
        </h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-slate-600 max-w-3xl">{t("scorecard.subtitle")}</p>

        <div className="bg-white border rounded-md overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="scorecard-table">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">{t("scorecard.col_dealer")}</th>
                <th className="text-left px-3 py-2">{t("scorecard.col_type")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_total")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_pending")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_approved")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_rejected")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_needs_changes")}</th>
                <th className="text-right px-3 py-2">{t("scorecard.col_ratio")}</th>
                <th className="px-3 py-2">{t("scorecard.col_flag")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">{t("aux.loading")}</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">{t("scorecard.empty")}</td></tr>
              )}
              {!loading && items.map(it => (
                <tr key={it.dealer_id || "direct"} data-testid={`scorecard-row-${it.dealer_id || "direct"}`}
                  className={it.flag ? "bg-red-50" : ""}>
                  <td className="px-3 py-2">
                    <div className="font-bold">{it.label || "—"}</div>
                    {it.dealer_id && <div className="text-[11px] text-slate-500 font-mono">{it.dealer_id}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{it.customer_type || "—"}</td>
                  <td className="px-3 py-2 text-right font-bold">{it.total}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{it.pending_review || 0}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{it.approved || 0}</td>
                  <td className="px-3 py-2 text-right text-red-700">{it.rejected || 0}</td>
                  <td className="px-3 py-2 text-right text-orange-700">{it.needs_changes || 0}</td>
                  <td className="px-3 py-2 text-right">
                    {it.approval_ratio == null
                      ? <span className="text-slate-400">—</span>
                      : <span className={it.flag ? "text-red-700 font-bold" : "text-slate-700"}>
                          {(it.approval_ratio * 100).toFixed(0)}%
                        </span>}
                  </td>
                  <td className="px-3 py-2">
                    {it.flag && (
                      <span data-testid={`scorecard-flag-${it.dealer_id || "direct"}`}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-300">
                        {t("scorecard.flag_low")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-500 max-w-3xl">{t("scorecard.footer_explainer")}</p>
      </main>
    </div>
  );
}
